/**
 * Mirrors Angular `StaffSelectionComponent` (transfer-process/staff-selection).
 * Role labels use the same mapping intent as `(user_roles | async).filter_user_roles` → React `getRoleDisplayName` / ROLE_DISPLAY_NAMES.
 */
import { useEffect, useState } from "react"
import { OnScreenModal } from "@/components/ui/OnScreenModal"
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from "@/components/ui/entityActionButtonClasses"
import { getRoleDisplayName } from "@/constants/roles"
import { formatPhoneDisplay } from "@/features/property/utils/formatPhone"
import type { PropertyStaffRow, StaffSelectionModalProps} from "../types"

function staffRowKey(staff: PropertyStaffRow): string {
  return `${staff.user_id ?? "nouser"}-${staff.role_id}`
}

export function StaffSelectionModal({
  isOpen,
  addedstaffList,
  transferstaffList,
  onClose,
  onSave,
}: StaffSelectionModalProps) {
  const [staffData, setStaffData] = useState<PropertyStaffRow | null>(null)

  /** Angular radios start unchecked each time the dialog is used. */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear the selected staff when the dialog closes (Angular parity: radios start unchecked each open)
    if (!isOpen) setStaffData(null)
  }, [isOpen])

  const hasRows = addedstaffList.length > 0 || transferstaffList.length > 0

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Primary Contact"
      onClose={onClose}
      maxWidth="max-w-2xl"
      overlayDataTestId="transfer-staff-selection-modal-overlay"
      panelDataTestId="transfer-staff-selection-modal-panel"
      titleDataTestId="transfer-staff-selection-modal-title"
      closeButtonDataTestId="transfer-staff-selection-modal-close-button"
    >
      <h6
        data-testid="transfer-staff-selection-modal-description"
        className="mb-4 text-sm font-medium text-slate-800"
      >
        No primary contact assigned, please assign one now.
      </h6>
      {hasRows ? (
        <div
          data-testid="transfer-staff-selection-table-container"
          className="max-h-[50vh] overflow-auto rounded border border-slate-200"
        >
          <table data-testid="transfer-staff-selection-table" className="min-w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-100 text-slate-700">
              <tr>
                <th className="px-3 py-2" data-testid="transfer-staff-selection-col-select">
                  Select
                </th>
                <th className="px-3 py-2" data-testid="transfer-staff-selection-col-name">
                  Name
                </th>
                <th className="px-3 py-2" data-testid="transfer-staff-selection-col-role">
                  Role
                </th>
                <th className="px-3 py-2" data-testid="transfer-staff-selection-col-phone">
                  Phone Number
                </th>
                <th className="px-3 py-2" data-testid="transfer-staff-selection-col-email">
                  Email
                </th>
              </tr>
            </thead>
            <tbody>
              {addedstaffList.map((staff) => (
                <tr
                  key={`a-${staffRowKey(staff)}`}
                  data-testid={`transfer-staff-selection-row-added-${staffRowKey(staff)}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-3 py-2">
                    <input
                      type="radio"
                      name="primary_staff"
                      data-testid={`transfer-staff-selection-radio-added-${staffRowKey(staff)}`}
                      checked={staffData !== null && staffRowKey(staffData) === staffRowKey(staff)}
                      onChange={() => setStaffData(staff)}
                    />
                  </td>
                  <td className="px-3 py-2">{staff.name ?? "—"}</td>
                  <td className="px-3 py-2">
                    {staff.role_name ? getRoleDisplayName(staff.role_name) : ""}
                  </td>
                  <td className="px-3 py-2">{formatPhoneDisplay(staff.mobile_no) || "—"}</td>
                  <td className="px-3 py-2">{staff.email ?? "—"}</td>
                </tr>
              ))}
              {transferstaffList.map((staff) => (
                <tr
                  key={`t-${staffRowKey(staff)}`}
                  data-testid={`transfer-staff-selection-row-transfer-${staffRowKey(staff)}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-3 py-2">
                    <input
                      type="radio"
                      name="primary_staff"
                      data-testid={`transfer-staff-selection-radio-transfer-${staffRowKey(staff)}`}
                      checked={staffData !== null && staffRowKey(staffData) === staffRowKey(staff)}
                      onChange={() => setStaffData(staff)}
                    />
                  </td>
                  <td className="px-3 py-2">{staff.name ?? "—"}</td>
                  <td className="px-3 py-2">
                    {staff.role_name ? getRoleDisplayName(staff.role_name) : ""}
                  </td>
                  <td className="px-3 py-2">{formatPhoneDisplay(staff.mobile_no) || "—"}</td>
                  <td className="px-3 py-2">{staff.email ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p
          data-testid="transfer-staff-selection-empty-message"
          className="rounded border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600"
        >
          No staff in the transfer list yet. Complete the employee step first, then try again.
        </p>
      )}
      {hasRows ? (
        <div
          data-testid="transfer-staff-selection-actions"
          className="mt-6 flex w-full flex-wrap items-center justify-center gap-3 pt-2"
        >
          <button
            type="button"
            disabled={!staffData}
            data-testid="transfer-staff-selection-save-button"
            onClick={() => {
              if (!staffData?.name) return
              onSave({
                name: String(staffData.name),
                mobile_no: String(staffData.mobile_no ?? "").replace(/\D/g, ""),
              })
            }}
            className={primaryEntityActionButtonClass}
          >
            Save
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="transfer-staff-selection-cancel-button"
            className={secondaryEntityActionButtonClass}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="mt-6 flex justify-center pt-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="transfer-staff-selection-close-button"
            className={secondaryEntityActionButtonClass}
          >
            Close
          </button>
        </div>
      )}
    </OnScreenModal>
  )
}
